import _get from "lodash/get"
import {
  getBadgeValue,
  getPDPUrlWithConfig,
  getSwatchCount,
  showNewBadge,
  formatFacetName,
  formatPriceRange,
  getUserPersona,
  getSwatchPreset,
  removeMavenoidChat,
  isCanadaSite,
  getPriceFieldFromFacet,
} from "@/utils/helper"
import _isEmpty from "lodash/isEmpty"
import { getPresetUrl, parseToBoolean } from "@/utils/index"
import {
  PRICE_RANGE,
  PriceRangeFrench,
  PRICE_RANGE_FRENCH_LOCALE,
  DISCOUNTED_PRICE_FACET_LABEL,
  SWATCH_NA,
} from "@/constants/index"
import {
  getAnalyticsDataForClearAll,
  getAnalyticsDataForFilterCloseOverlay,
  getAnalyticsDataForViewAllResultsCTA,
  addPrintOrShareAnalytics,
  addCollapseAnalytics,
  getFilterAnalyticsForTagClearCTA,
  getTrackingAttributeForClearAllCTA,
  getShowHideFilterOptionAnalyticsData,
  getTrackingAttributesForSort,
  getAnalyticsDataForSortByClick,
  getAnalyticsDataForSocialShare,
} from "@/components/ProductList/v3/analytics/PLPFilterAnalytics"
import announceForAccessibility from "@/utils/announceForAccessibility"
import {
  FACET_FORMAT_REGEX,
  PIPE_NOT_PRECEDED_BY_BACKSLASH_REGEX,
} from "@/constants/regex"
const facetFieldsColor = "ColorFinish_s"

const getViewportWidth = () => {
  if (typeof window !== "undefined") {
    return window.innerWidth
  }
  // Default width for server-side rendering
  return 1024
}

const extractProductName = (
  item,
  isSterling,
  sterlingData,
  isGrouped,
  type
) => {
  if (isSterling && sterlingData?.length > 0) {
    return sterlingData[0]?.["ProductBrandName_s"] || ""
  }

  if (isGrouped && item?.["Collapse_Title_s"]) {
    return item["Collapse_Title_s"]
  }

  if (type?.toLowerCase() === "parts") {
    return item?.["productName_s"] || ""
  }

  return item?.["RegionBrandName_s"] || item?.["productName_s"] || ""
}

// calculate price for data layer
const calculateDataLayerPrices = (item, persona, colors) => {
  const price = item?.[`priceStartingAt.${persona}_d`] ?? null
  const isDiscountedPrice =
    item?.[`priceList.${persona}.discountedPrice_d`] ?? ""
  const discountAmount = price - isDiscountedPrice || 0
  const badgeVal = item?.[`priceList.${persona}.saleOffer_s`] ?? ""
  const discountPercent =
    badgeVal && isDiscountedPrice
      ? 100 - Math.round((isDiscountedPrice / price) * 100)
      : 0
  const globalPromotionPrice =
    discountPercent && discountAmount < price
      ? Number((price - discountAmount).toFixed(2))
      : price
  const productIsRetail = item?.["ProductIsRetail"] ?? false
  return {
    globalPromotionPrice,
    priceState:
      badgeVal && isDiscountedPrice
        ? "percentOff|" + discountPercent
        : "regular price",
    productBasePrice: price,
    productSalePrice: isDiscountedPrice || price,
    productSaleable: !(productIsRetail || !colors.length),
  }
}

/**
 * Processes PLP (Product Listing Page) data and transforms it into a standardized format
 * @param {Object} inputObj - The input object containing raw PLP data
 * @param {boolean} isSterling - Flag indicating if the data is for Sterling
 * @param {Array} sterlingData - Array containing Sterling-specific data
 * @param {string} type - The type of products being processed
 * @param {Object} configParams - Additional params for transformation
 * @param {Object} plpPresetConfig - for image settings
 * @return {Object} Processed PLP data with standardized structure
 */
export const getPlpProcessedData = async (
  inputObj = {},
  isSterling = false,
  sterlingData = [],
  type = "",
  configParams = {},
  plpPresetConfig = {}
) => {
  const newData = {}
  const isGrouped = inputObj.grouped
  const isCollapsePLP = inputObj?.fusion?.isCollapsePLP ?? false
  const persona = getUserPersona()
  // First process raw data
  Object.entries(inputObj).forEach(([key, value]) => {
    if (Array.isArray(value) && key === "data") {
      newData.data = value.map(item => {
        // Extract productName logic
        const productName = extractProductName(
          item,
          isSterling,
          sterlingData,
          isGrouped,
          type
        )

        // Extract pricing information
        const fieldValue = item?.["Collapse_Field_s"]
        const swatchColor = inputObj?.PlpSwatches.filter(
          swatchObj => swatchObj[fieldValue]
        )

        let colors
        if (inputObj?.fusion?.isCollapsePLP && swatchColor.length) {
          colors = swatchColor[0][fieldValue]
        } else if (item?.["Color.SKU.Details_ss"]) {
          colors = item?.["Color.SKU.Details_ss"]
        } else {
          colors = item?.[`Color.${persona}.Details_ss`] || []
        }

        const {
          globalPromotionPrice,
          priceState,
          productBasePrice,
          productSalePrice,
          productSaleable,
        } = calculateDataLayerPrices(item, persona, colors)
        return {
          isCollapsePLP: isCollapsePLP,
          isSterling: isSterling,
          originalData: item,
          isGrouped: isGrouped,
          description:
            isGrouped &&
            item?.["Collapse_Description_s"] &&
            !item?.["sku_s"].includes("BNDL")
              ? item?.["Collapse_Description_s"]
              : (() => {
                  const shortDescription =
                    item?.["ProductDescriptionProductShort_s"]
                  return (
                    (shortDescription || item?.["Collapse_Description_s"]) ?? ""
                  )
                })(),
          colors: colors,
          collapseField: item?.["Collapse_Field_s"]
            ? item?.["Collapse_Field_s"]
            : null,

          resourceNvznId: item?.["ResourceNvznId_s"]
            ? item?.["ResourceNvznId_s"]
            : null,
          regionIsBackOrderable: item?.["RegionIsBackOrderable_b"]
            ? item?.["RegionIsBackOrderable_b"]
            : false,
          resourceVntanaProductId: item?.["ResourceVntanaProductId_s"]
            ? item?.["ResourceVntanaProductId_s"]
            : null,
          resourceVntanaProductIdList: item?.["ResourceVntanaProductIdList_ss"]
            ? item?.["ResourceVntanaProductIdList_ss"]
            : [],
          resourceNvznIdList: item?.["ResourceNvznIdList_ss"]
            ? item?.["ResourceNvznIdList_ss"]
            : [],
          productIsExclusive: item?.["ProductIsExclusive_s"]
            ? item?.["ProductIsExclusive_s"]
            : null,
          slug: item?.["slug_s"] ? item?.["slug_s"] : null,
          productNo: item?.["ProductProductNo_s"]
            ? item?.["ProductProductNo_s"]
            : null,
          resourceName: item?.["ProductResource.InteractiveSVG.resourceName_ss"]
            ? item?.["ProductResource.InteractiveSVG.resourceName_ss"]
            : "",
          resourceTitle:
            item?.["ProductResource.InteractiveSVG.resourceTitle_ss"] ?? "",
          productName: productName ?? null,
          productTileName: productName ?? null,
          category:
            item?.["Product_Category"] ??
            item?.["ProductLocalCategory_s"] ??
            "",
          shipmentDate: item?.["RegionReleaseforShipment_dt"] ?? "",
          badgeValue: item?.[`priceList.${persona}.saleOffer_s`] ?? "",
          productIsRetail: item?.["ProductIsRetail"] === "true",
          isSuperSku: Boolean(_get(item, "comboSKUAvailable_b", false)),
          productRoom: _get(item, "category1_s", "n/a"),
          productCollectionsName: _get(
            item,
            "ProductBrandNameDisplay_s",
            "n/a"
          ),
          productVariantImages: item?.["productImages.labelWithUrl_ss"]
            ? item?.["productImages.labelWithUrl_ss"]
            : [],
          sku:
            item?.["Bundle_s"] === "Yes" && item?.["variantList.sku_ss"]?.[0]
              ? item?.["variantList.sku_ss"][0]
              : item?.["sku_s"] ?? item?.["masterSKU_s"],
          masterCustomerFacingSku: item?.["masterCustomerFacingSKU_s"] ?? "",
          productCategory: item?.["Product_Category"] ?? "",
          isBundleProduct:
            Boolean(item?.["primaryBundleSKU_s"]) ||
            item?.["ctProductType_s"] === "static-bundle-parent",
          bundlePLPImageURL: item?.["BundlePLPImageURL_s"] ?? null,
          bundleFlag: item?.["Bundle_s"] ?? null,
          displayPriceStartingAt: item?.["min_price"] ?? null,
          globalPromotionPrice,
          priceState,
          productBasePrice,
          productSalePrice,
          productSaleable,
          allVariantsDiscontinued: Boolean(
            item?.["productRegionAllVariantDiscontinued_b"]
          ),
        }
      })
    }
  })

  // Now transform the processed data into card format
  try {
    const transformedCards = await Promise.resolve(
      transformProductCards(
        newData?.data,
        configParams.configdata,
        configParams.swatchUrl,
        configParams.plpServletPath,
        configParams.general,
        null,
        plpPresetConfig
      )
    )

    newData.facets = inputObj.facets
    newData.facetLabels = inputObj.facetLabels
    newData.fusion = inputObj.fusion
    newData.total = inputObj.total
    newData.colors = inputObj.colors
    newData.isGrouped = isGrouped

    return {
      ...newData,
      transformedCards,
    }
  } catch (error) {
    console.error(
      "Error transforming product cards in getPlpProcessedData:",
      error
    )
    return {
      ...newData,
      transformedCards: [],
    }
  }
}

/**
 * Transforms product data into card format for display
 * @param {Array} products - Array of product objects to transform
 * @param {Object} configdata - Configuration data for product cards
 * @param {string} swatchUrl - Base URL for color swatch images
 * @param {string} plpServletPath - PLP servlet path for URL construction
 * @param {Object} general - General configuration settings
 * @param {Object} selectedColor - Currently selected color information
 * @param {Object} plpPresetConfig - Currently selected color information
 * @return {Array} Array of transformed product card objects
 */
export const transformProductCards = async (
  products,
  configdata,
  swatchUrl,
  plpServletPath,
  general,
  selectedColor,
  plpPresetConfig
) => {
  const width = getViewportWidth()
  try {
    // Input validation
    if (!products || !Array.isArray(products)) {
      return []
    }
    // Transform products
    return products
      .map(product => {
        // Validate product
        if (!product) return null
        // Get SKU - use variantList.sku_ss[0] for bundles, otherwise use masterCustomerFacingSku or product.sku
        let sku = "K-" + product.sku || ""

        // For bundle products, use variantList.sku_ss[0] if available
        if (
          product.bundleFlag === "Yes" &&
          product.originalData?.["variantList.sku_ss"]?.[0]
        ) {
          sku = product.originalData["variantList.sku_ss"][0]
        }

        if (!sku) return null
        try {
          // Process product data
          // if selectColor the fetch sku based on that

          const selectedSku = product?.colors?.find(
            color => color?.split("|")[5] === selectedColor?.customerFacingSKU
          )
          if (selectedSku) sku = selectedSku?.split("|")[5]

          const allowedColorCount = getSwatchCount(true)
          const allColorsData = colorFetcheslogic(product, width, swatchUrl)
          let extraColors = 0
          if (allColorsData.length > allowedColorCount) {
            extraColors = allColorsData.length - allowedColorCount
          }
          const colors = allColorsData?.slice(0, allowedColorCount) || []
          const badgesdata = getBadgesData(product, configdata) || []
          const url = getProductUrl(product, plpServletPath, general)
          const fullUrl =
            typeof window === "undefined"
              ? `${process.env.NEXT_PUBLIC_GLOBALCONFENV}${url}`
              : `${window.location.origin}${url}`
          const isBundle = product.bundleFlag === "Yes"
          let swatchImageId = ""
          let swatchImages =
            product?.isCollapsePLP &&
            !isBundle &&
            !product.isBundleProduct &&
            product?.originaldata?.[`colorSkuSlugListDetails_ss`].length > 0
              ? product?.originaldata[`colorSkuSlugListDetails_ss`]
              : product?.variantImages

          swatchImages = swatchImages?.find(img =>
            selectedColor
              ? selectedColor.sku === img.split("|")[0]
              : product.sku === img.split("|")[0]
          )

          let imgUrl = ""
          let defaultImageName = ""
          if (swatchImages) {
            swatchImages = swatchImages?.split("|")
            const swatchIndex =
              product?.isCollapsePLP && !isBundle && !product.isBundleProduct
                ? swatchImages.length - 2
                : swatchImages.length - 1

            const swatchImageValues = swatchImages[swatchIndex]?.split("/")
            swatchImageId = swatchImageValues[swatchImageValues.length - 1]
            imgUrl = getPresetUrl(
              width,
              width <= 1024 ? 2 : 3,
              general.swatchUrl,
              swatchImageId,
              plpPresetConfig
            )
            defaultImageName = swatchImageId
          }
          // Create product object
          const newProductObj = {
            type: "Product Card",
            isGrouped: product.isGrouped,
            isCollapsePLP: product.isCollapsePLP,
            isSterling: product.isSterling,
            sterlingData: product.sterlingData || [],
            variantImages: product.productVariantImages,
            variantColors: product.colors,
            product: product,
            originaldata: product.originalData,
            fullUrl: fullUrl,
            defaultImageName: defaultImageName || "",
            image: {
              src: imgUrl,
              alt: product.productName || "Product Image",
              reveal: "default",
              loading: "blank",
            },
            isSaved: false,
            saveCTA: null,
            title: {
              text: product.productName || "",
            },
            description: {
              text: product.description || "",
            },

            productDetails: {
              colorSwatches: {
                colors,
                extraColors: extraColors,
                selectedColor: null,
                onColorSelected: null,
              },
              tags: badgesdata,
              SKU: { text: sku },
            },
          }
          return newProductObj
        } catch (error) {
          console.log(`Error processing product ${sku}:`, error)
          return null
        }
      })
      .filter(Boolean) // Remove null values
  } catch (error) {
    console.error("Error in transformProductCards:", error)
    return []
  }
}

/**
 * Processes color data for product swatches
 * @param {Object} data - Product data containing color information
 * @param {number} width - Screen width for responsive image URLs
 * @param {string} swatchUrl - Base URL for color swatch images
 * @return {Array} Array of processed color swatch objects
 */
export function colorFetcheslogic(data, width, swatchUrl) {
  let colorsTemp = []
  if (data.colors) {
    colorsTemp = data.colors.map(color => {
      const d = color?.split("|")
      // Use desktop preset for server-side rendering
      const preset = getSwatchPreset(width)
      const imgURL = `${swatchUrl}${d[1]}${preset}`
      const imgObj = {
        src: imgURL,
        alt: d[0],
        reveal: "default",
        loading: "blank",
      }
      const colorObj = {
        colorName: d[0],
        title: d[0],
        image: imgObj,
        shape: "square",
        size: "small",
        swatch: d[1],
        sku: d[2],
        isStock: d[3],
        isDiscontinued: d[4],
        customerFacingSKU: d[5],
        id: d[2],
        // All event handlers will be attached client-side
        onMouseEnter: null,
        onMouseLeave: null,
        productData: {
          sku: d[2],
          customerFacingSKU: d[5],
          image: imgURL,
          colorName: d[0],
          isStock: d[3],
        },
      }
      return colorObj
    })
  }

  const colorsTempVal = colorsTemp.filter(
    item =>
      item.swatch?.toLowerCase() !== SWATCH_NA.toLowerCase() &&
      (item.isDiscontinued === "false" ||
        Date.parse(item.isDiscontinued) > Date.now())
  )
  return colorsTempVal
}

/**
 * Checks if a product has 3D visualization capabilities
 * @param {Object} data - Product data containing 3D resource information
 * @return {boolean} True if product has 3D resources available
 */
const isThreeDBadgeAvailable = data => {
  return (
    data["resourceNvznId"] ||
    data["resourceVntanaProductId"] ||
    data["resourceVntanaProductIdList"]?.length ||
    data["resourceNvznIdList"]?.length
  )
}

/**
 * Gets badge data based on product information and configuration
 * @param {Object} product - Product object containing badge-related properties
 * @param {Object} configdata - Configuration object containing badge settings
 * @return {Array} Array of badge objects for display
 */
export const getBadgesData = (product, configdata) => {
  const plpBadges = getdefaultBades(configdata.plpBadgeList)
  const abc = plpBadges.filter(item => {
    const isNew = item.key.includes("new") && showNewBadge(product.shipmentDate)
    const isSale = item.key.includes("sale") && !_isEmpty(product.badgeValue)
    const is3D = item.key.includes("3d") && isThreeDBadgeAvailable(product)
    const isExclusive =
      item.key.includes("exclusive") &&
      (parseToBoolean(product.productIsExclusive) ||
        parseToBoolean(product?.["ProductIsExclusive_s"]))
    const isSet =
      item.key.includes("bundleBadge") &&
      (product.bundleFlag === "Yes" || product.Bundle_s === "Yes")
    return isNew || isSale || is3D || isExclusive || isSet
  })
  return abc
}

/**
 * Processes the badge list to get default badges configuration
 * @param {Array} plpBadgeList - List of available badges with their configurations
 * @return {Array} Array of processed badge objects with label and key properties
 */
function getdefaultBades(plpBadgeList) {
  const displayOrderBadges =
    plpBadgeList.find(item => item.map.Key === "displayOrderBadges") ?? {}
  const {
    map: { Values: { myArrayList: displayOrderBadgeList = [] } = {} } = {},
  } = displayOrderBadges

  const plpBadges = []
  displayOrderBadgeList.forEach(item => {
    const badgeData = getBadgeValue(item, plpBadgeList)
    plpBadges.push({ label: badgeData.value, key: badgeData.key }) // variant:badgeData.key,
  })
  return plpBadges
}

/**
 * Constructs the product URL based on product data and configuration
 * @param {Object} product - Product object containing category and slug
 * @param {string} plpServletPath - PLP servlet path for URL construction
 * @param {Object} general - General configuration settings
 * @return {string} Constructed product URL
 */
const getProductUrl = (product, plpServletPath, general) => {
  if (!plpServletPath?.length || !product?.category || !product?.slug) {
    return ""
  }
  return getPDPUrlWithConfig(
    product.category,
    product.slug,
    plpServletPath,
    general
  )
}

// Helper Function
/**
 * Finds the facet field key by its label.
 * @param {string} label - The label to search for (may contain underscores).
 * @param {Object} labelsObj - An object mapping facet field keys to their labels.
 * @return {string|undefined} The facet field key if found, otherwise undefined.
 */
export function getFacetFieldByLabel(label, labelsObj) {
  if (label === "Price_Range") {
    return "Price_Range"
  } else {
    // Replace underscores with spaces for comparison
    const normalizedLabel = label.replace(/_/g, " ")
    return Object.keys(labelsObj).find(
      key => labelsObj[key].replace(/_/g, " ") === normalizedLabel
    )
  }
}

/**
 * Constructs the product URL based on product data and configuration
 * @param {string} facetString - If URL has query params as facets, should render cards accordingly from serverside
 * @param {string} facetLabels - If URL has query params as facets, should render cards accordingly from serverside
 * @param {string} persona - The user persona used for facet processing
 * @param {boolean} isCanadaCheck - Flag indicating if the Canada site check should be applied
 * @return {string} Constructed API query params for fq
 */
export function reformatFacetString(
  facetString,
  facetLabels,
  persona,
  isCanadaCheck
) {
  if (!facetString) return []

  // Remove the prefix "facet:'" and the trailing "'"
  const cleaned = facetString.replace(FACET_FORMAT_REGEX, "")

  // Split into key-value pairs
  const pairs = cleaned?.split(",")

  // Transform each pair
  const formatted = pairs.map(pair => {
    const [key, value] = pair.split(":")
    if (!key || !value) return ""
    // Find the facet field using facet_labels
    let tagKey = getFacetFieldByLabel(key, facetLabels) || key

    // If Price_Range, get label from facetFieldWithLabel
    if (tagKey === "Price_Range" || key === "Price_Range") {
      tagKey = getPriceFieldFromFacet(facetLabels, persona) || tagKey
      // No quotes for Price_Range values
      // Split by unescaped pipe and join with OR
      const values = value
        ?.split(PIPE_NOT_PRECEDED_BY_BACKSLASH_REGEX)
        .map(v => v.trim().replace(/"/g, '\\"'))
        .join(" OR ")
        .replaceAll(String.raw`\|`, "|")
      return encodeURIComponent(`{!tag="${tagKey}"}${tagKey}:(${values})`)
    } else {
      // Quotes for all other keys
      // Split by unescaped pipe and join with OR
      const values = value
        ?.split(PIPE_NOT_PRECEDED_BY_BACKSLASH_REGEX)
        .map(v => `"${v.trim().replace(/"/g, '\\"')}"`)
        .join(" OR ")
        .replaceAll(String.raw`\|`, "|")

      return encodeURIComponent(`{!tag="${tagKey}"}${tagKey}:(${values})`)
    }
  })

  return formatted
}

/**
 * Reformats a sort string using the facetFieldWithLabel mapping.
 * If the sort string exists in the mapping, returns the mapped value; otherwise, returns the original sort string.
 * @param {string} sortString - The sort string to reformat.
 * @param {string} persona - The user persona used for sort field mapping.
 * @param {boolean} isCanadaCheck - Flag indicating if the Canada site check should be applied.
 * @return {string} The reformatted sort string or the original if no mapping exists.
 */
export function reformatSortString(sortString, persona, isCanadaCheck) {
  if (!sortString) return null
  return getFacetFieldWithLabel(sortString, persona, isCanadaCheck)
}

/**
 * Gets facet field mappings based on params and persona using switch statement
 * @param {string} params - The facet parameter to map
 * @param {string} persona - User persona (default: "GST")
 * @param {boolean} isCanadaCheck - Flag indicating if the Canada site check should be applied
 * @return {string} The mapped facet field value
 */
export const getFacetFieldWithLabel = (
  params,
  persona = "GST",
  isCanadaCheck = false
) => {
  switch (params) {
    case "Price_Range":
      if (isCanadaCheck) {
        return `priceList.${persona}.price_d`
      }
      return `priceList.${persona}.finalPrice_d`

    case "price desc":
      if (isCanadaCheck) {
        return `priceList.${persona}.price_d desc`
      }
      return `priceList.${persona}.finalPrice_d desc`

    case "price asc":
      if (isCanadaCheck) {
        return `priceList.${persona}.price_d asc`
      }
      return `priceList.${persona}.finalPrice_d asc`

    case "featured":
      return ""

    default:
      return null
  }
}

/**
 * Checks if a key-value pair is present in an array of "key - value" strings.
 * @param {string[]} array - The array of strings in the format "key - value".
 * @param {string} attributeName - The key to search for.
 * @param {string} attributeValue - The value to search for.
 * @return {boolean} True if the key-value pair exists, false otherwise.
 */
export function isKeyValuePresent(array, attributeName, attributeValue) {
  return array.some(item => {
    const [key, value] = item.split(" - ").map(str => str.trim())
    return key === attributeName && value === attributeValue
  })
}

/**
 * Returns the input field variant type based on the facet field.
 * @param {string} item - The facet field name.
 * @return {string} The input field variant ("colorSwatchCheckbox" for color facets, otherwise "checkbox").
 */
const getInptfieldsVarient = item => {
  if (item === facetFieldsColor) {
    return "colorSwatchCheckbox"
  } else {
    return "checkbox"
  }
}

/**
 * Determines if the given facet field represents a price range filter.
 * @param {string} filterFacet - The facet field name to check.
 * @return {boolean} True if the facet is a price range filter, false otherwise.
 */
export const isPriceRangeFilter = filterFacet => {
  return (
    filterFacet &&
    (filterFacet === PRICE_RANGE ||
      filterFacet === PriceRangeFrench ||
      filterFacet === PRICE_RANGE_FRENCH_LOCALE ||
      filterFacet.includes(DISCOUNTED_PRICE_FACET_LABEL))
  )
}

/**
 * Finds and returns formatted attribute values for a given facet and filter query.
 * @param {Array} dataArray - The facet data array, where the first element is the facet name and the second is the values array.
 * @param {Object} facetLabels - An object mapping facet field keys to their labels.
 * @param {string} attributeName - The attribute (facet) name to match.
 * @param {string} attributeValue - The attribute value to match.
 * @param {string} filterQuery - The filter query string (e.g., "ColorFinish_s:White|Black").
 * @return {string[]} An array of formatted "Facet - Value" strings matching the criteria.
 */
const findAttributeValue = (
  dataArray,
  facetLabels,
  attributeName,
  attributeValue,
  filterQuery
) => {
  // Handle empty filter query with specific attribute
  if (filterQuery === "" && attributeName !== "" && attributeValue !== "") {
    return handleEmptyFilterQuery(dataArray, attributeName, attributeValue)
  }

  // Handle filter query processing
  if (filterQuery !== "") {
    return processFilterQuery(dataArray, facetLabels, filterQuery)
  }

  return []
}

/**
 * Handles the case when filter query is empty but attribute name and value are provided
 * @param {Array} dataArray - The facet data array
 * @param {string} attributeName - The attribute name to match
 * @param {string} attributeValue - The attribute value to match
 * @return {string[]} Array of formatted attribute values
 */
const handleEmptyFilterQuery = (dataArray, attributeName, attributeValue) => {
  if (!Array.isArray(dataArray) || dataArray.length < 2) {
    return []
  }

  const facetName = dataArray[0].replace(/\*\*/g, "")
  if (facetName !== attributeName) {
    return []
  }

  const valuesArray = dataArray[1]
  for (let i = 0; i < valuesArray.length; i += 2) {
    if (valuesArray[i] === attributeValue) {
      return [`${attributeName} - ${valuesArray[i]}`]
    }
  }

  return []
}

/**
 * Processes the filter query string to extract matching facet values
 * @param {Array} dataArray - The facet data array
 * @param {Object} facetLabels - Mapping of facet field keys to labels
 * @param {string} filterQuery - The filter query string
 * @return {string[]} Array of formatted facet values
 */
const processFilterQuery = (dataArray, facetLabels, filterQuery) => {
  if (!Array.isArray(dataArray) || dataArray.length < 2) {
    return []
  }

  const facets = splitFilterQuery(filterQuery)
  const result = []
  const targetFacetName = dataArray[0].replace(/\*\*/g, "")

  for (const facet of facets) {
    const facetResults = processSingleFacet(
      facet,
      facetLabels,
      targetFacetName,
      result
    )
    result.push(...facetResults)
  }

  return [...new Set(result)]
}

/**
 * Splits the filter query into individual facet groups
 * @param {string} filterQuery - The filter query string
 * @return {string[]} Array of individual facet strings
 */
const splitFilterQuery = filterQuery => {
  return filterQuery.includes(",") ? filterQuery.split(",") : [filterQuery]
}

/**
 * Processes a single facet string and returns matching formatted values
 * @param {string} facet - Single facet string (e.g., "ColorFinish_s:White|Black")
 * @param {Object} facetLabels - Mapping of facet field keys to labels
 * @param {string} targetFacetName - The target facet name to match against
 * @param {string[]} existingResults - Already processed results to avoid duplicates
 * @return {string[]} Array of formatted facet values for this facet
 */
const processSingleFacet = (
  facet,
  facetLabels,
  targetFacetName,
  existingResults
) => {
  const facetParts = parseFacetString(facet)
  if (!facetParts) {
    return []
  }

  const { facetName, facetValues } = facetParts
  const labelKey = getFacetFieldByLabel(facetName, facetLabels) || facetName
  const finalKey = labelKey || facetName

  // Only process if this facet matches our target
  if (targetFacetName !== finalKey) {
    return []
  }

  return formatFacetValues(finalKey, facetValues, existingResults)
}

/**
 * Parses a facet string into name and values
 * @param {string} facet - Single facet string
 * @return {Object|null} Object with facetName and facetValues, or null if invalid
 */
const parseFacetString = facet => {
  const [facetName, facetValueRaw] = facet?.split(":") ?? []

  if (!facetName || !facetValueRaw) {
    return null
  }
  // Split values by unescaped pipe and clean up
  const facetValues = facetValueRaw
    ?.split(PIPE_NOT_PRECEDED_BY_BACKSLASH_REGEX)
    .map(v => v.trim().replaceAll(String.raw`\|`, "|"))

  return {
    facetName,
    facetValues,
  }
}

/**
 * Formats facet values into the required string format
 * @param {string} finalKey - The final facet key to use
 * @param {string[]} facetValues - Array of facet values
 * @param {string[]} existingResults - Already processed results to avoid duplicates
 * @return {string[]} Array of formatted facet values
 */
const formatFacetValues = (finalKey, facetValues, existingResults) => {
  const results = []

  for (const singleValue of facetValues) {
    const formatted = `${finalKey} - ${singleValue}`
    if (!existingResults.includes(formatted)) {
      results.push(formatted)
    }
  }

  return results
}

/**
 * Formats an array of facet strings into a grouped and delimited string for analytics or API usage.
 * Groups values by their facet label and outputs as "Label:Value1|Value2,...".
 * @param {string[]} inputArray - Array of facet strings in the format "Facet - Value".
 * @param {Object} labels - An object mapping facet field keys to their display labels.
 * @param {string} attributeName - The facet name to prioritize in sorting.
 * @param {string} attributeValue - The facet value to prioritize in sorting.
 * @return {string} The formatted string, e.g., "Collections:Antilia|Beauxline,Material:Cast Iron".
 */
export function formatFacets(
  inputArray,
  labels,
  attributeName,
  attributeValue
) {
  const grouped = {}
  // Sort inputArray to prioritize the matching attribute
  const sortedArray = [...inputArray].sort((a, b) => {
    const [keyA, valueA] = a.split(" - ")
    const [keyB, valueB] = b.split(" - ")
    const isMatchA = keyA === attributeName && valueA === attributeValue
    const isMatchB = keyB === attributeName && valueB === attributeValue
    if (isMatchA && !isMatchB) return -1
    if (!isMatchA && isMatchB) return 1
    return 0
  })
  sortedArray.forEach(item => {
    let [key, value] = item.split(" - ")

    // Escape pipe characters in value
    if (String(value).includes("|")) {
      value = `${value.replace(/\|/g, "\\|")}`
    }

    const label = labels[key] || key
    if (label) {
      const formattedLabel = label.replace(/\s+/g, "_")
      if (!grouped[formattedLabel]) {
        grouped[formattedLabel] = []
      }
      grouped[formattedLabel].push(value)
    }
  })
  return Object.entries(grouped)
    .map(([label, values]) => `${label}:${values.join("|")}`)
    .join(",")
}

// facets is an object with facet names as keys and arrays of values as values
// facetLabels is an object with facet names as keys and their display labels as values

export const getUniversalUrl = () => {
  const isClient = typeof window !== "undefined"

  if (isClient) {
    return {
      fullUrl: window.location.href,
    }
  }
}

/**
 * Create desktop-only focus/blur handlers for the filter panel.
 * @param {boolean} isMobileViewport - true when viewport is mobile; handlers are no-ops on mobile.
 * @return {Object} An object containing optional `onFocus` and `onBlur` handlers.
 */
const createDesktopScrollHandlers = isMobileViewport => {
  if (isMobileViewport) return {}

  return {
    onFocus: () => {
      const filterPanel = document.querySelector(".kohler-scrollarea-viewport")
      if (filterPanel) {
        filterPanel.style.paddingBottom = "90px"
        try {
          filterPanel.scrollTo({
            top: filterPanel.scrollHeight - filterPanel.clientHeight,
            behavior: "smooth",
          })
        } catch (e) {
          filterPanel.scrollTop =
            filterPanel.scrollHeight - filterPanel.clientHeight
        }
      }
    },
    onBlur: () => {
      const filterPanel = document.querySelector(".kohler-scrollarea-viewport")
      if (filterPanel) {
        filterPanel.style.paddingBottom = ""
      }
    },
  }
}

const prepareShareIconObjectFilter = (
  plpLocalState,
  t,
  linkCopied,
  setLinkCopied,
  isMobileViewport,
  isShareCTA
) => {
  const {
    twitter: { map: { redirectUrl: twitterRedirectUrl } = {} } = {},
    facebook: { map: { redirectUrl: facebookRedirectUrl } = {} } = {},
    linkedin: { map: { redirectUrl: linkedinRedirectUrl } = {} } = {},
  } = plpLocalState
  const handlers = createDesktopScrollHandlers(isMobileViewport)
  return {
    sharePopover: {
      withinPortal: false,
      ...(handlers.onFocus && { onFocus: handlers.onFocus }),
      ...(handlers.onBlur && { onBlur: handlers.onBlur }),
      links: [
        {
          logo: "facebook",
          tabIndex: isShareCTA ? 0 : -1,
          "aria-hidden": !isShareCTA,
          href: `${facebookRedirectUrl}${getUniversalUrl()?.fullUrl}`,
          "data-social-share": "facebook",
          trackingAttributes: getAnalyticsDataForSocialShare(
            false,
            "facebook",
            `${facebookRedirectUrl}${getUniversalUrl()?.fullUrl}`,
            "share"
          ),
        },
        {
          logo: "twitter",
          tabIndex: isShareCTA ? 0 : -1,
          "aria-hidden": !isShareCTA,
          href: `${twitterRedirectUrl}${getUniversalUrl()?.fullUrl}`,
          "data-social-share": "twitter",
          trackingAttributes: getAnalyticsDataForSocialShare(
            false,
            "twitter",
            `${twitterRedirectUrl}${getUniversalUrl()?.fullUrl}`,
            "share"
          ),
        },
        {
          logo: "linkedin",
          tabIndex: isShareCTA ? 0 : -1,
          "aria-hidden": !isShareCTA,
          href: `${linkedinRedirectUrl}${getUniversalUrl()?.fullUrl}`,
          "data-social-share": "linkedin",
          trackingAttributes: getAnalyticsDataForSocialShare(
            false,
            "linkedin",
            `${linkedinRedirectUrl}${getUniversalUrl()?.fullUrl}`,
            "share"
          ),
        },
      ],
      copyCTA: {
        label: !linkCopied
          ? t("kf.share.label.copyLink")
          : t("kf.share.label.copyLinkIndication"),
        tabIndex: isShareCTA ? 0 : -1,
        "aria-hidden": !isShareCTA,
        onClick: e => {
          setLinkCopied(true)

          // Set timer for 3 seconds to reset linkCopied state
          setTimeout(() => {
            setLinkCopied(false)
          }, 3000)
          navigator.clipboard.writeText(`${getUniversalUrl()?.fullUrl}`)
          getAnalyticsDataForSocialShare(
            true,
            t("kf.share.label.copyLink"),
            `${getUniversalUrl()?.fullUrl}`
          )
        },
      },
      emailCTA: {
        label: t("kf.share.label.email"),
        tabIndex: isShareCTA ? 0 : -1,
        "aria-hidden": !isShareCTA,
        onClick: () => {
          const subject = encodeURIComponent("Check out this product page")
          const body = encodeURIComponent(`${getUniversalUrl()?.fullUrl}`)
          window.location.href = `mailto:?subject=${subject}&body=${body}`
          getAnalyticsDataForSocialShare(true, t("kf.share.label.email"), `n/a`)
        },
      },
      showDivider: true,
    },
  }
}

/**
 * Formats and returns the props object for PLP filter UI, including analytics handlers and filter logic.
 * @param {Object} plpLocalState - The local state containing facets, facetLabels, filterQuery, colorLabels, plpTotal, and selectedFilterArray.
 * @param {boolean} isFilterVisible - Whether the filter overlay is currently visible.
 * @param {Function} setIsFilterVisible - Function to toggle filter overlay visibility.
 * @param {Object} componentProps - Additional component props (attributeName, attributevalue, swatchUrl).
 * @param {Object} t - Static text labels (clearAll, viewResults, etc.).
 * @param {Function} handleServerSidedataOnchange - Handler for server-side data changes.
 * @param {Function} handleActiveFiltersChange - Handler for active filter changes.
 * @param {boolean} linkCopied - Indicates if the link has been copied to clipboard.
 * @param {Function} setLinkCopied - Function to set the linkCopied state.
 * @param {Function} refreshFilters - Function to refresh the filters.
 * @param {Function} isFromVisualNav - Function to check if the request is from visual navigation.
 * @param {Function} isFromCheckBoxClickedRef - Function to check if the request is from checkbox click.
 * @param {Function} dispatch - Function to dispatch actions to the Redux store.
 * @param {Function} setFilterQueryState - Function to set the filter query state.
 * @param {Function} setIsFromVisualNav - Function to set the visual navigation state.
 * @param {Function} setPageState - Function to set the page state.
 * @param {boolean} isMobileViewport - Boolean flag indicating if the current viewport is mobile.
 * @param {Function} setAnnounceResultsAfterClear - Function to set the announce state after clearing filters.
 * @param {boolean} isShareCTA - Indicates if the share CTA is currently active.
 * @param {Function} setIsShareCTA - Function to set the share CTA state.
 * @return {Object} The formatted props object for the PLP filter component.
 */
export const plpFilterArrayFormatter = (
  plpLocalState,
  isFilterVisible,
  setIsFilterVisible,
  componentProps,
  t,
  handleServerSidedataOnchange,
  handleActiveFiltersChange,
  linkCopied,
  setLinkCopied,
  refreshFilters,
  isFromVisualNav,
  isFromCheckBoxClickedRef,
  dispatch,
  setFilterQueryState,
  setIsFromVisualNav,
  setPageState,
  isMobileViewport,
  setAnnounceResultsAfterClear,
  isShareCTA,
  setIsShareCTA
) => {
  const { facets, facetLabels, filterQuery, colorLabels, plpTotal } =
    plpLocalState
  const { attributeName, attributevalue, swatchUrl } = componentProps

  if (!facets || Object.keys(facets).length === 0) {
    return {}
  }
  const filterFields = Object?.entries(facets).filter(
    facet => facet[1].length > 0
  )
  const filterItemsArray = filterFields.map(facet => ({
    title: isPriceRangeFilter(facet[0])
      ? t("kf.plp.label.priceRangeTitle")
      : formatFacetName(facet[0], null, facetLabels),
    accordionProps: {
      toggleCallback: (isOpen, title) => {
        addCollapseAnalytics(isOpen, title.text, "products", "Products")
        handleServerSidedataOnchange()
        isFromCheckBoxClickedRef.current = true
      },
    },
    facetName: facet[0],
    filterItems: {
      variant: getInptfieldsVarient(facet[0].replace(/\*\*/g, "")),
      defaultValues: findAttributeValue(
        facet,
        facetLabels,
        attributeName,
        attributevalue,
        filterQuery
      ),
      items: Array.from({ length: facet[1].length / 2 }, (_, i) => ({
        label: isPriceRangeFilter(facet[0])
          ? formatPriceRange(facet[1][i * 2], false)
          : `${facet[1][i * 2]}`,
        ...(facet[1][i * 2 + 1] && { count: facet[1][i * 2 + 1] }),

        value: `${facet[0].replace(/\*\*/g, "")} - ${facet[1][i * 2]}`,

        ...(facet[0].replace(/\*\*/g, "") === facetFieldsColor && {
          image: {
            src: `${swatchUrl}${
              colorLabels[facet[1][i * 2]]
            }?$PLPSwatch_Desk100$`,
            alt: `${facet[0]} - ${facet[1][i * 2]}`,
            reveal: "default",
            loading: "blank",
          },
        }),
      })),
    },
  }))
  const propsFilters = {
    filterItemsGroups: [
      ...filterItemsArray.toSorted((a, b) => {
        if (a.facetName === "Price_Range") return -1
        if (b.facetName === "Price_Range") return 1
        return 0
      }),
    ],
    refreshFilters: isFromVisualNav && refreshFilters,
    closeCTA: {
      onClick: () => {
        setIsFilterVisible(false)
        getAnalyticsDataForFilterCloseOverlay()
      },
    },

    onActiveFiltersChange: e => {
      handleActiveFiltersChange(e)
    },
    onMouseOver: e => {
      handleServerSidedataOnchange()
    },
    tagFilterProps: {
      clearCTA: {
        onClick: e => {
          isFromCheckBoxClickedRef.current = false
          const selectedFilters = plpLocalState?.selectedFilterArray || []
          if (selectedFilters.length === 0) return // Use the last filter as the one being removed

          const lastTag = selectedFilters[selectedFilters.length - 1]
          const [facet, value] = lastTag.split(" - ")

          const filterArray = selectedFilters
            .map(item => {
              const [facet, value] = item.split(" - ")
              return `${
                isPriceRangeFilter(facet)
                  ? t("kf.plp.label.priceRangeTitle")
                  : facetLabels[facet]
              }:${isPriceRangeFilter(facet) ? formatPriceRange(value) : value}`
            })
            .join("|")

          getFilterAnalyticsForTagClearCTA(
            filterArray,
            isPriceRangeFilter(facet) ? formatPriceRange(value) : value,
            isPriceRangeFilter(facet)
              ? t("kf.plp.label.priceRangeTitle")
              : facetLabels[facet]
          )
        },
      },
    },
    isOverlayOpen: isFilterVisible,
    clearAllCTA: {
      text: t("kf.plp.label.clearAll"),
      onClick: () => {
        isFromCheckBoxClickedRef.current = false
        getAnalyticsDataForClearAll()
        setPageState("clearAll")
        dispatch(setFilterQueryState("NONE"))
        dispatch(setIsFromVisualNav(false))
        // Announce to screen readers that filters were cleared
        try {
          announceForAccessibility(
            t("kf.plp.announce.clearAllSuccess") ||
              "Cleared all filters successfully",
            () => {
              setAnnounceResultsAfterClear(true)
            }
          )
        } catch (e) {
          /* ignore */
        }
      },
      trackingAttributes: {
        "data-datalayer-id": "kds-element-plp-filters-clearAll",
        "data-gbh-data-layer": getTrackingAttributeForClearAllCTA(),
      },
    },
    shareCTA: {
      label: t("kf.plp.label.share"),
      onClick: () => {
        setIsShareCTA(!isShareCTA)
        addPrintOrShareAnalytics("share", "Products", "products")
      },
    },
    ...prepareShareIconObjectFilter(
      plpLocalState,
      t,
      linkCopied,
      setLinkCopied,
      isMobileViewport,
      isShareCTA
    ),
    CTAViewResult: {
      text: `${t("kf.plp.label.viewResults")} (${plpTotal})`,
      onClick: () => {
        setIsFilterVisible(!isFilterVisible)
        getAnalyticsDataForViewAllResultsCTA()
      },
    },
    printCTA: {
      label: t("kf.plp.label.print"),
      onClick: () => {
        window.print()
        addPrintOrShareAnalytics("print", "Products", "products")
      },
      ...createDesktopScrollHandlers(isMobileViewport),
    },
  }
  return propsFilters
}

const getFilterLabel = (isFilterVisible, t, isMobileViewport) => {
  if (isFilterVisible) {
    return t("kf.plp.label.hideFilter")
  }

  if (isMobileViewport) {
    return t("kf.plp.label.filterSort")
  }

  return t("kf.plp.label.showFilter")
}

export const plpSortArrayFormatter = (
  plpLocalState,
  t,
  isFilterVisible,
  setIsFilterVisible,
  selectSort,
  isGrouped,
  setPlpLocalState,
  isMobileViewport,
  dispatch,
  setServerData
) => {
  const propsSortUI = {
    variant: "default",
    resultsCount: `${plpLocalState.plpTotal} ${t("kf.plp.label.results")}`,
    filtersCTA: {
      label: getFilterLabel(isFilterVisible, t, isMobileViewport),
      onClick: () => {
        setIsFilterVisible(!isFilterVisible)
        getShowHideFilterOptionAnalyticsData(!isFilterVisible)
        removeMavenoidChat()
      },
    },
    sortDropdownProps: {
      defaultValue: selectSort,
      options: [
        {
          label: t("kf.plp.label.featured"),
          value: "featured",
          trackingAttributes: {
            "data-datalayer-id": "kds-element-plp-sort",
            "data-gbh-data-layer": getTrackingAttributesForSort(
              t("kf.plp.label.featured")
            ),
          },
        },
        {
          label: t("kf.plp.label.priceDesc"),
          value: "price desc",
          trackingAttributes: {
            "data-datalayer-id": "kds-element-plp-sort",
            "data-gbh-data-layer": getTrackingAttributesForSort(
              t("kf.plp.label.priceDesc")
            ),
          },
        },
        {
          label: t("kf.plp.label.priceAsc"),
          value: "price asc",
          trackingAttributes: {
            "data-datalayer-id": "kds-element-plp-sort",
            "data-gbh-data-layer": getTrackingAttributesForSort(
              t("kf.plp.label.priceAsc")
            ),
          },
        },
        {
          label: t("kf.plp.label.atoz"),
          value: `${
            isGrouped ? "CollapseSort_s asc" : "NonCollapseSort_s asc"
          }`,
          trackingAttributes: {
            "data-datalayer-id": "kds-element-plp-sort",
            "data-gbh-data-layer": getTrackingAttributesForSort(
              t("kf.plp.label.atoz")
            ),
          },
        },
        {
          label: t("kf.plp.label.ztoa"),
          value: `${
            isGrouped ? "CollapseSort_s desc" : "NonCollapseSort_s desc"
          }`,
          trackingAttributes: {
            "data-datalayer-id": "kds-element-plp-sort",
            "data-gbh-data-layer": getTrackingAttributesForSort(
              t("kf.plp.label.ztoa")
            ),
          },
        },
      ],
      onSelect: e => {
        setPlpLocalState(prev => ({
          ...prev,
          selectSort: e,
        }))
        dispatch(setServerData(false))
        getAnalyticsDataForSortByClick(
          propsSortUI.sortDropdownProps.options.find(
            sortOption => sortOption.value === e
          )?.label
        )
      },
      placeholder: t("kf.plp.label.sort"),
    },
  }
  return propsSortUI
}

export const formatPrice = (price, currencySign) => {
  if (!price || isNaN(price) || price <= 0) return null

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  })

  return `${currencySign}${formatter.format(Number(price))}`
}

export const calculateProductPricing = (
  propsProductCard = {},
  persona = "",
  general = {},
  currencySign = "$",
  isServerData = "true"
) => {
  const {
    product: { originalData = {} },
    isGrouped = false,
    isSterling = false,
    sterlingData = [],
  } = propsProductCard

  const isCanadaCheck = isCanadaSite(general?.siteName)

  // Helper function to format price range
  const formatPriceRange = (minPrice, maxPrice) => {
    const formattedMin = formatPrice(minPrice, currencySign)
    const formattedMax = formatPrice(maxPrice, currencySign)

    if (!formattedMin) return null
    if (!formattedMax || minPrice === maxPrice) return formattedMin

    return `${formattedMin} - ${formattedMax}`
  }

  // Extract base pricing from product data
  let discountedPrice = originalData[`priceList.${persona}.finalPrice_d`] || 0
  let finalPrice = originalData[`priceList.${persona}.price_d`] || 0
  const skuVariantPrice = originalData[`Color.${persona}.Details_ss`] || []

  let maxPrice = 0
  let discountedMaxPrice = 0

  // Handle grouped originalData pricing
  if (isGrouped && !isServerData) {
    finalPrice = originalData.min_price || 0
    maxPrice = originalData.max_price || 0
    discountedPrice = originalData.disc_min_price || 0
    discountedMaxPrice = originalData.disc_max_price || 0
    if (isCanadaCheck) {
      finalPrice = originalData.min_price || 0
      maxPrice = originalData.max_price || 0
    }
  }
  if (isGrouped && isServerData) {
    discountedPrice = originalData[`priceList.${persona}.discMinPrice_d`] || 0
    discountedMaxPrice =
      originalData[`priceList.${persona}.discMaxPrice_d`] || 0
    finalPrice = originalData[`priceList.${persona}.minPrice_d`] || 0
    maxPrice = originalData[`priceList.${persona}.maxPrice_d`] || 0
    if (isCanadaCheck) {
      finalPrice = originalData[`priceList.${persona}.minPrice_d`] || 0
      maxPrice = originalData[`priceList.${persona}.maxPrice_d`] || 0
    }
  }

  // Handle Sterling originalData pricing override
  if (isSterling && sterlingData.length > 0) {
    const sterlingPrice = sterlingData[0]?.[`priceList.${persona}.finalPrice_d`]
    if (sterlingPrice) {
      finalPrice = sterlingPrice
      discountedPrice = sterlingPrice
    }
  }

  // Check if commerce is disabled
  const isCommerceDisabled = general?.disableCommerce

  // When commerce is disabled, modify the pricing logic
  if (isCommerceDisabled) {
    // When commerce is disabled, always use regular prices (finalPrice)
    // and never show discounted prices

    // Determine if there's a price range (using regular prices only)
    let hasPriceRange = false
    if (isGrouped) {
      hasPriceRange = finalPrice !== maxPrice
    }

    // Calculate display price using only regular prices
    let displayPrice = null
    if (finalPrice > 0) {
      if (isGrouped) {
        displayPrice = formatPriceRange(finalPrice, maxPrice)
      } else {
        displayPrice = formatPrice(finalPrice, currencySign)
      }
    }

    return {
      displayPrice, // Always regular price when commerce disabled
      slashedPrice: null, // Always null when commerce disabled
      hasPriceRange, // Based on regular price range only
      skuVariantPrice, // SKU variant price details
    }
  }

  // Original commerce-enabled logic
  // Determine if there's a valid discount
  const hasDiscount =
    !isCommerceDisabled && discountedPrice > 0 && discountedPrice !== finalPrice

  // Determine if there's a price range
  let hasPriceRange = false
  if (isGrouped) {
    if (hasDiscount) {
      hasPriceRange = discountedPrice !== discountedMaxPrice
    } else {
      hasPriceRange = finalPrice !== maxPrice
    }
  }

  // Calculate display price
  let displayPrice = null
  if (finalPrice > 0) {
    if (isGrouped) {
      const minDisplayPrice = hasDiscount ? discountedPrice : finalPrice
      const maxDisplayPrice = hasDiscount ? discountedMaxPrice : maxPrice
      displayPrice = formatPriceRange(minDisplayPrice, maxDisplayPrice)
    } else {
      const singleDisplayPrice = hasDiscount ? discountedPrice : finalPrice
      displayPrice = formatPrice(singleDisplayPrice, currencySign)
    }
  }

  // Calculate slashed price (original price when on discount)
  let slashedPrice = null
  if (hasDiscount && finalPrice > 0) {
    if (isGrouped) {
      slashedPrice = formatPriceRange(finalPrice, maxPrice)
    } else {
      slashedPrice = formatPrice(finalPrice, currencySign)
    }
  }

  return {
    displayPrice, // Current price to show (discounted if available, otherwise regular)
    slashedPrice, // Original price with strikethrough (only when discounted)
    hasPriceRange, // Boolean indicating if product has price range
    skuVariantPrice, // SKU variant price details
  }
}
